import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { Sparkles, Loader2, ArrowRight, ShieldCheck, Send, CheckCircle2 } from "lucide-react";
import { useOrganizerAuth } from "../auth/OrganizerAuthContext";
import PasswordInput from "../components/PasswordInput";
import axios from "axios";

const API_BASE = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function OrganizerSignup() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const { signup } = useOrganizerAuth();

  const [form, setForm] = useState({
    name: "",
    contact_name: "",
    email: "",
    phone: "",
    password: "",
    city: "",
    state: "",
    event_type: "",
    expected_attendees: "",
    phone_otp_code: "",
  });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  // Phase 8.20 — OTP state machine: idle → sending → sent (code input shown) → verifying → done
  const [otpState, setOtpState] = useState("idle"); // idle | sent | resending
  const [otpMsg, setOtpMsg] = useState("");
  const [otpCountdown, setOtpCountdown] = useState(0);

  const set = (k) => (e) => setForm((s) => ({ ...s, [k]: e.target.value }));

  const requestOtp = async () => {
    setError("");
    setOtpMsg("");
    if (!form.phone || form.phone.replace(/\D/g, "").length < 10) {
      setError("Vui lòng nhập số điện thoại hợp lệ trước khi lấy mã OTP.");
      return;
    }
    setOtpState(otpState === "sent" ? "resending" : "sending");
    try {
      const r = await axios.post(`${API_BASE}/organizer/signup/request-phone-otp`, {
        phone: form.phone,
        preferred_language: "vi",
      });
      setOtpState("sent");
      setOtpMsg(
        r.data?.detail === "preview_short_circuit"
          ? "Mã OTP đã tạo (chế độ preview — không thực sự gửi SMS)."
          : "Đã gửi mã OTP tới điện thoại của bạn."
      );
      // Start 60s cooldown so users can't spam Resend
      setOtpCountdown(60);
      const iv = setInterval(() => {
        setOtpCountdown((v) => {
          if (v <= 1) { clearInterval(iv); return 0; }
          return v - 1;
        });
      }, 1000);
    } catch (err) {
      setOtpState("idle");
      const d = err?.response?.data?.detail;
      setError(typeof d === "string" ? d : (d?.message || "Không thể gửi OTP. Vui lòng kiểm tra số điện thoại."));
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (form.password.length < 8) {
      setError("Mật khẩu phải có ít nhất 8 ký tự.");
      return;
    }
    if (!form.phone_otp_code || form.phone_otp_code.length < 4) {
      setError("Vui lòng nhập mã OTP đã được gửi tới điện thoại của bạn.");
      return;
    }
    setBusy(true);
    try {
      const payload = { ...form };
      payload.expected_attendees = form.expected_attendees
        ? parseInt(form.expected_attendees, 10)
        : null;
      Object.keys(payload).forEach((k) => {
        if (payload[k] === "" || payload[k] === null) delete payload[k];
      });
      await signup(payload);
      const next = params.get("next") || "/organizer/dashboard";
      navigate(next, { replace: true });
    } catch (err) {
      const d = err?.response?.data?.detail;
      setError(typeof d === "string" ? d : (d?.message || "Đăng ký thất bại. Vui lòng thử lại."));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-linen flex items-center justify-center px-4 py-12" data-testid="organizer-signup-page">
      <div className="w-full max-w-2xl">
        <Link to="/" className="inline-flex items-center gap-2 text-espresso-soft hover:text-espresso text-sm">
          <Sparkles className="w-4 h-4" />
          <span className="tracking-[0.28em] uppercase">Nail Viet Club</span>
        </Link>

        <h1 className="mt-6 text-4xl sm:text-5xl font-serif text-espresso">Đăng ký tổ chức sự kiện</h1>
        <p className="mt-2 text-espresso-soft">
          Tạo tài khoản miễn phí để bắt đầu tổ chức sự kiện của bạn trên Nail Viet Club.
        </p>

        <form onSubmit={submit} className="mt-8 bg-white rounded-3xl p-6 sm:p-8 shadow-sm border border-espresso/10 space-y-5">
          <Row>
            <Field label="Tên đơn vị / công ty *" testId="org-name" value={form.name} onChange={set("name")} required placeholder="VD: Nail Pros Houston Inc." />
            <Field label="Người liên hệ *" testId="org-contact-name" value={form.contact_name} onChange={set("contact_name")} required placeholder="Họ và tên" />
          </Row>
          <Row>
            <Field label="Email *" type="email" testId="org-email" value={form.email} onChange={set("email")} required />
            <Field label="Số điện thoại *" testId="org-phone" value={form.phone} onChange={set("phone")} required placeholder="+1 832 555 0199" />
          </Row>
          <Row>
            <Field label="Thành phố" testId="org-city" value={form.city} onChange={set("city")} placeholder="Houston" />
            <Field label="Tiểu bang" testId="org-state" value={form.state} onChange={set("state")} placeholder="TX" />
          </Row>
          <Row>
            <Field label="Loại sự kiện" testId="org-event-type" value={form.event_type} onChange={set("event_type")} placeholder="Triển lãm, hội thảo, lễ hội…" />
            <Field label="Số khách dự kiến" type="number" testId="org-expected" value={form.expected_attendees} onChange={set("expected_attendees")} placeholder="500" />
          </Row>
          <Field label="Mật khẩu (tối thiểu 8 ký tự) *" type="password" testId="org-password" value={form.password} onChange={set("password")} required minLength={8} />

          {/* Phase 8.20 — Phone OTP verification. User taps "Gửi mã OTP"
              once they've filled the phone field, then enters the 6-digit
              code below to complete signup. */}
          <div
            data-testid="org-otp-block"
            className={`rounded-2xl border p-4 space-y-3 ${
              otpState === "sent"
                ? "border-emerald-200 bg-emerald-50/50"
                : "border-espresso/15 bg-linen-100/40"
            }`}
          >
            <div className="flex items-center justify-between gap-3 flex-wrap">
              <div className="flex items-center gap-2">
                <ShieldCheck className="w-4 h-4 text-espresso-soft" />
                <span className="text-[11px] uppercase tracking-[0.22em] font-semibold text-espresso-soft">
                  Xác minh số điện thoại
                </span>
              </div>
              <button
                type="button"
                onClick={requestOtp}
                disabled={otpState === "sending" || otpState === "resending" || otpCountdown > 0}
                data-testid="org-otp-request"
                className={`inline-flex items-center gap-1.5 rounded-full px-4 py-1.5 text-xs font-semibold transition ${
                  otpState === "sent"
                    ? "border border-emerald-300 text-emerald-700 hover:bg-emerald-100 disabled:opacity-40"
                    : "bg-espresso text-cream hover:bg-espresso-hover disabled:opacity-50"
                }`}
              >
                {otpState === "sending" || otpState === "resending" ? (
                  <Loader2 className="w-3.5 h-3.5 animate-spin" />
                ) : otpState === "sent" ? (
                  <CheckCircle2 className="w-3.5 h-3.5" />
                ) : (
                  <Send className="w-3.5 h-3.5" />
                )}
                {otpCountdown > 0
                  ? `Gửi lại sau ${otpCountdown}s`
                  : otpState === "sent"
                  ? "Gửi lại mã"
                  : "Gửi mã OTP"}
              </button>
            </div>
            {otpMsg && (
              <p data-testid="org-otp-msg" className="text-[12px] text-emerald-700">{otpMsg}</p>
            )}
            {otpState === "sent" && (
              <input
                type="text"
                inputMode="numeric"
                pattern="[0-9]*"
                maxLength={8}
                autoComplete="one-time-code"
                data-testid="org-otp-code"
                value={form.phone_otp_code}
                onChange={set("phone_otp_code")}
                placeholder="Nhập 6 số OTP"
                className="w-full bg-white border border-emerald-300 rounded-xl px-4 py-3 text-lg tabular-nums tracking-[0.4em] text-center font-semibold text-espresso focus:outline-none focus:ring-2 focus:ring-emerald-400"
              />
            )}
          </div>

          {error && (
            <div data-testid="org-signup-error" className="rounded-xl bg-rose-50 border border-rose-200 px-4 py-3 text-sm text-rose-700">
              {error}
            </div>
          )}

          <button
            type="submit"
            disabled={busy || otpState !== "sent"}
            data-testid="org-signup-submit"
            className="w-full inline-flex items-center justify-center gap-2 bg-espresso text-cream rounded-full py-3 font-semibold tracking-wide disabled:opacity-50"
          >
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <ShieldCheck className="w-4 h-4" />}
            {busy
              ? "Đang tạo tài khoản…"
              : otpState !== "sent"
              ? "Vui lòng lấy mã OTP trước"
              : "Tạo tài khoản & vào dashboard"}
            {!busy && otpState === "sent" && <ArrowRight className="w-4 h-4" />}
          </button>

          <p className="text-center text-sm text-espresso-soft">
            Đã có tài khoản?{" "}
            <Link to="/organizer/login" className="text-espresso font-medium underline">Đăng nhập</Link>
          </p>
        </form>
      </div>
    </div>
  );
}

function Row({ children }) {
  return <div className="grid sm:grid-cols-2 gap-5">{children}</div>;
}

function Field({ label, testId, type = "text", ...rest }) {
  if (type === "password") {
    return (
      <label className="block">
        <span className="text-[10px] uppercase tracking-[0.22em] font-semibold text-espresso-soft">{label}</span>
        <PasswordInput
          data-testid={testId}
          testIdSuffix={testId || "field-password"}
          {...rest}
          wrapperClassName="mt-1"
          leftIcon={null}
          className="w-full bg-linen-100/60 border border-espresso/10 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-espresso"
        />
      </label>
    );
  }
  return (
    <label className="block">
      <span className="text-[10px] uppercase tracking-[0.22em] font-semibold text-espresso-soft">{label}</span>
      <input
        type={type}
        data-testid={testId}
        {...rest}
        className="mt-1 w-full bg-linen-100/60 border border-espresso/10 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-espresso"
      />
    </label>
  );
}
